/*
 * This file is part of the Det Norske Teatret application.
 *
 * (c) APT AS
 *
 * For the full copyright and license information, please view the LICENSE
 * file that was distributed with this source code.
 */

import React, { useRef, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import throttle from 'lodash/throttle';
import Picture from 'components/Picture/Picture';
import useAnimation from './animations';
import './DoubleTitle.scss';

function useMaxSize() {
  const ref = useRef(null);
  const [size, setSize] = useState(null);

  useEffect(() => {
    if (size === null) {
      const style = window
        .getComputedStyle(ref.current, null)
        .getPropertyValue('font-size');
      const fontSize = parseFloat(style) / 16;

      setSize(fontSize);
    }
  }, [size]);

  return [ref, size];
}

/**
 * This is the DoubleTitle component.
 *
 * @author Thomas Sømoen <thomas@apt.no>
 *
 * @return {JSX}
 */
function DoubleTitle({ heading, image, author, photographer }) {
  const [ref] = useAnimation();
  const container = useRef(null);
  const [h1, maxSize] = useMaxSize();
  const [size, setSize] = useState(null);

  useEffect(() => {
    const onResize = throttle(() => {
      setSize(null);
    }, 200);

    window.addEventListener('resize', onResize);

    return () => {
      window.removeEventListener('resize', onResize);
    };
  }, []);

  useEffect(() => {
    if (maxSize !== null) {
      if (
        container.current.offsetHeight > window.innerHeight ||
        container.current.offsetWidth > window.innerWidth
      ) {
        setSize(prev => {
          if (prev === null) {
            return maxSize;
          }

          if (prev - 1 > 2) {
            return prev - 1;
          }

          return prev;
        });
      }
    }
  }, [maxSize, size]);

  function getStyle() {
    if (!size) return null;

    return {
      fontSize: `${size}em`,
    };
  }

  function renderPhotographer() {
    if (!photographer) return null;

    return (
      <>
        {' // '}
        <span className="photo">{photographer}</span>
      </>
    );
  }

  return (
    <header ref={ref} className={`header double-title`} style={getStyle()}>
      <div ref={container}>
        <div className="title">
          <h1 ref={h1}>
            <span dangerouslySetInnerHTML={{ __html: heading }} />
            <span
              dangerouslySetInnerHTML={{ __html: heading }}
              role="presentation"
            />
          </h1>
          <div className="image">
            <Picture {...image} />
          </div>
        </div>
        <div className="copyright">
          <span className="author">{author}</span>
          {renderPhotographer()}
        </div>
      </div>
    </header>
  );
}

/**
 * Declare expected prop types.
 *
 * @type {Object}
 */
DoubleTitle.propTypes = {
  heading: PropTypes.string.isRequired,
  image: PropTypes.object.isRequired,
  author: PropTypes.string,
  photographer: PropTypes.string,
};

/**
 * Declare defaults for non-required props.
 *
 * @type {Object}
 */
DoubleTitle.defaultProps = {
  image: {},
  author: null,
  photographer: null,
};

export default DoubleTitle;
